export type SoundType = 'flip' | 'match' | 'mismatch' | 'steal' | 'click' | 'win' | 'start';

const BGM_URL =
  'https://nu.vgmtreasurechest.com/soundtracks/the-legend-of-zelda-breath-of-the-wild/pzbhivdg/1-02.%20Main%20Theme.mp3';

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  type WebkitWindow = typeof window & { webkitAudioContext?: typeof AudioContext };
  const W = window as WebkitWindow;
  const Ctor = window.AudioContext ?? W.webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

export function resumeAudio(): void {
  const c = getCtx();
  if (c && c.state === 'suspended') void c.resume();
}

export function playSound(type: SoundType, enabled: boolean): void {
  if (!enabled) return;
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === 'suspended') void c.resume();
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain);
    gain.connect(c.destination);
    const now = c.currentTime;
    switch (type) {
      case 'flip':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.exponentialRampToValueAtTime(600, now + 0.1);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
        break;
      case 'match':
        osc.type = 'square';
        osc.frequency.setValueAtTime(987, now);
        osc.frequency.setValueAtTime(1318, now + 0.1);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.4);
        osc.start(now);
        osc.stop(now + 0.4);
        break;
      case 'mismatch':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.linearRampToValueAtTime(100, now + 0.2);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
        break;
      case 'steal':
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(600, now);
        osc.frequency.exponentialRampToValueAtTime(1200, now + 0.15);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.15);
        osc.start(now);
        osc.stop(now + 0.15);
        break;
      case 'click':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(800, now);
        gain.gain.setValueAtTime(0.05, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.05);
        osc.start(now);
        osc.stop(now + 0.05);
        break;
      case 'win': {
        const notes = [523.25, 659.25, 783.99, 1046.5, 783.99, 1046.5];
        notes.forEach((f, i) => {
          const o = c.createOscillator();
          const g = c.createGain();
          o.connect(g);
          g.connect(c.destination);
          o.type = 'square';
          o.frequency.value = f;
          const t = c.currentTime + i * 0.12;
          g.gain.setValueAtTime(0.1, t);
          g.gain.exponentialRampToValueAtTime(0.01, t + 0.1);
          o.start(t);
          o.stop(t + 0.1);
        });
        break;
      }
      case 'start':
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.setValueAtTime(880, now + 0.1);
        gain.gain.setValueAtTime(0.05, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.6);
        osc.start(now);
        osc.stop(now + 0.6);
        break;
    }
  } catch {
    /* 音效失敗靜默忽略 */
  }
}

let bgmAudio: HTMLAudioElement | null = null;
let bgmPlaying = false;

export const bgm = {
  play(enabled: boolean): void {
    if (!enabled || bgmPlaying) return;
    try {
      if (!bgmAudio) {
        bgmAudio = new Audio(BGM_URL);
        bgmAudio.loop = true;
        bgmAudio.volume = 0.3;
      }
      void bgmAudio.play();
      bgmPlaying = true;
    } catch {
      /* noop */
    }
  },
  stop(): void {
    bgmPlaying = false;
    if (bgmAudio) {
      try {
        bgmAudio.pause();
        bgmAudio.currentTime = 0;
      } catch {
        /* noop */
      }
    }
  },
};
